import { getFormGroups } from '../../services/addFormApi';
import { getDefaults } from '../../services/detailDefaults';
import { groupInProgressConfig, inProgressVerdict } from './educationRules';

// =============================================================================
// The in-progress gate, asked from OUTSIDE a form
// -----------------------------------------------------------------------------
// A region rule that only lives inside the submission form is a rule you meet
// too late. Quick Submit picks a candidate, hands them to the submission form,
// and the form prefills their education straight off the candidate record — so
// a candidate the rule refuses gets all the way to a form that will not submit,
// with nothing on screen explaining why and no way back except cancelling.
//
// This asks the same question at the point of selection. Deliberately using the
// SAME resolver the form uses (educationRules.inProgressVerdict) against the
// SAME config (the submission module's groups): a second implementation of
// "does this candidate have an unfinished qualification" would drift, and the
// drift shows up as a popup that lets someone through to a refusal.
//
// Nothing here names a module, a field or a region.
// =============================================================================

// The target module's groups are config, not data — identical for every
// candidate checked in a session. Fetching them once per module keeps a
// dropdown that checks each selection from making a config call per keystroke.
const groupsCache = new Map();

/** Drops the cached config. Call after an admin saves Form Groups. */
export function clearInProgressGateCache() {
  groupsCache.clear();
}

async function targetGroups(module) {
  if (!module) return [];
  if (!groupsCache.has(module)) {
    // The PROMISE is cached, not the result, so several selections racing at
    // once share one request instead of firing several identical ones.
    groupsCache.set(module, getFormGroups({ module }).catch((err) => {
      // A config read that fails must not block a submit. Losing the gate is
      // recoverable — the form still refuses at submit time — whereas refusing
      // every candidate because a request timed out is not.
      groupsCache.delete(module);
      console.warn(`[in-progress gate] could not read ${module} form groups:`, err);
      return [];
    }));
  }
  return groupsCache.get(module);
}

/**
 * gateNeedsRecord — does this module restrict anything at all?
 *
 * Answered BEFORE the record is fetched, so a tenant whose region allows
 * everything (which is most of them) pays nothing: no candidate lookup, no
 * added latency on a dropdown.
 */
export function gateNeedsRecord(groups = [], region) {
  return groups.some((group) => {
    const cfg = groupInProgressConfig(group, region);
    return cfg && cfg.severity !== 'allow';
  });
}

/**
 * checkInProgress — may this record be put forward into `targetModule`?
 *
 *   targetModule  the module being submitted INTO (submissions), whose rules
 *                 decide. Never the source module — a candidate's own rules
 *                 are what allowed the row to exist in the first place.
 *   sourceModule  the module the record belongs to (candidates)
 *   recordId      that record's id
 *
 * Returns { severity: 'allow' | 'warn' | 'block', message, group }.
 * Always resolves — a failure anywhere returns 'allow', because the submission
 * form enforces this again and a gate that fails closed would make an outage
 * look like a policy.
 */
export async function checkInProgress({ targetModule, sourceModule, recordId, region } = {}) {
  const allow = { severity: 'allow', message: '', group: null };
  if (!targetModule || !sourceModule || !recordId) return allow;

  const activeRegion = region ?? getDefaults().region;
  try {
    const groups = await targetGroups(targetModule);
    if (!gateNeedsRecord(groups, activeRegion)) return allow;

    // Only now is the record worth fetching. getFormGroups with an id embeds
    // each repeatable group's stored rows, which is exactly what the verdict
    // needs — and it is the same read the edit form uses, so the rows judged
    // here are the rows that would be prefilled.
    const sourceGroups = await getFormGroups({ module: sourceModule, id: recordId });
    const record = {};
    for (const group of sourceGroups) {
      const key = group?.payloadKey || group?.name;
      if (key && Array.isArray(group?.rows)) record[key] = group.rows;
    }
    return inProgressVerdict(groups, activeRegion, record);
  } catch (err) {
    console.warn('[in-progress gate] check skipped:', err);
    return allow;
  }
}
